<template>
    <component
        :is="ad.is_listed ? 'a' : 'div'"
        :href="ad.ad_url"
        class="list-item"
        :class="{
            'list-item--is-admin': hasOwnerActions,
            'featured': isFeatured
        }">

        <CompactGallery
            :images="ad.media"
            :showFloorplans="ad.floorplans.length > 0"
            @image-clicked="openAdUrl"
            class="">
            <template #extras>
                <div class="list-item__type-container">
                    <strong class="list-item__type featured" v-if="isFeatured">
                        Featured
                    </strong>
                    <strong class="list-item__type" v-if="ad.status_added_formatted">
                        {{ ad.status_added_formatted }}
                    </strong>
                    <strong v-if="ad.rent_cost_reduced_formatted" class="list-item__type" style="color: green; border:1px solid green;">
                        Reduced
                    </strong>
                    <strong v-if="ad.views_count > 50" class="list-item__type">
                        Popular
                    </strong>
                    <strong v-if="ad.is_let_agreed" class="list-item__let_agreed">
                        LET AGREED
                    </strong>
                </div>
            </template>
        </CompactGallery>

        <DoubleGallery
            v-if="withDoubleGallery"
            :images="ad.media"
            :showFloorplans="ad.floorplans.length > 0"
            @image-clicked="openAdUrl">
            <template #extras>
                <div class="list-item__type-container">
                    <strong class="list-item__type featured" v-if="isFeatured">
                        Featured
                    </strong>
                    <strong class="list-item__type" v-if="ad.status_added_formatted">
                        {{ ad.status_added_formatted }}
                    </strong>
                    <strong v-if="ad.rent_cost_reduced_formatted" class="list-item__type" style="color: green; border:1px solid green;">
                        Reduced
                    </strong>
                    <strong v-if="ad.views_count > 50" class="list-item__type">
                        Popular
                    </strong>
                    <strong v-if="ad.is_let_agreed" class="list-item__let_agreed">
                        LET AGREED
                    </strong>
                </div>
            </template>
        </DoubleGallery>

        <div class="list-item__meta">
            <div class="list-item__header">
                <div class="list-item__header-left">
                    <h4 class="list-item__title">
                        {{ ad.title }}
                    </h4>
                    <p class="list-item__address mb-0">
                        {{ ad.full_address_with_postcode || ad.area_label }}
                    </p>
                </div>
                <div class="list-item__header-right">
                    <template v-if="ad.rent_cost_reduced_formatted">
                        <p class="h4 list-item__price">
                            <span>{{ priceFormatted }} </span> <br class="over-desktop-only" />
                            <span> {{ rentTypeFormatted == 'month' ? 'pcm' : 'pw' }} </span>
                        </p>
                        <span class="price-reduced-label">{{ ad.rent_cost_reduced_formatted }}</span>
                    </template>
                    <p v-else class="h4 list-item__price">
                        <span>{{ priceFormatted }} </span> <br class="over-desktop-only" />
                        <span> {{ rentTypeFormatted == 'month' ? 'pcm' : 'pw' }}</span>
                    </p>
                </div>
            </div>
            <div class="d-flex justify-content-between my-3">
                <AdIcons :icons="ad.icons"/>
                <FavButton
                    v-if="hasFavButton && ad.auth_user.can_add_to_favorites"
                    v-model="isFavorite"
                    :ad-slug="ad.slug"
                />
            </div>
            <div class="d-flex flex-row justify-content-between gap-2 mb-3">
                <div
                    class="list-item__description"
                    v-html="ad.description_short_formatted"/>
                <p class="list-item__image over-phone-only p-1" v-if="hasPostedBy && ad.company_image_url">
                    <img v-once :src="ad.company_image_url"/>
                </p>
            </div>

            <div class="list-item__footer">
                <p class="list-item__date">
                    <template v-if="ad.is_published">
                        Published {{ changeDateFormat(ad.published_at) || ad.published_at }}
                    </template>
                    <template v-else-if="ad.is_let_agreed">
                        Let agreed {{ changeDateFormat(ad.unpublished_at) || ad.unpublished_at }}
                    </template>
                    <template v-else-if="ad.unpublished_at">
                        Unpublished {{ changeDateFormat(ad.unpublished_at) || ad.unpublished_at }}
                    </template>
                    <template v-else>
                        Never published
                    </template>
                </p>
                <p class="list-item__date" v-if="ad.is_want_room && ad.is_published">
                    <template v-if="ad.move_in_earliest_formatted || ad.move_in_latest_formatted">
                        Move in
                        <template v-if="ad.move_in_earliest_formatted && ad.move_in_earliest_formatted !== 'Now'">
                            from {{ ad.move_in_earliest_formatted }}
                        </template>
                        <template v-if="ad.move_in_latest_formatted">
                            until {{ ad.move_in_latest_formatted }}
                        </template>
                    </template>
                    <template v-else-if="ad.available_from_date_formatted">
                        Move in {{ ad.available_from_date_formatted }}
                    </template>
                </p>
                <p class="list-item__image less-phone-only" v-else-if="hasPostedBy && ad.company_image_url">
                    <img v-once :src="ad.company_image_url"/>
                </p>
            </div>
        </div>

        <AdCardUserActions
            v-if="hasOwnerActions"
            :ad="ad"
            :agents="agents"
            :is-selected="isSelected"
            :has-renew-button="hasRenewButton"
            :has-posted-by="hasPostedBy"
            @selected="$emit('selected', $event)"
            @updated="$emit('updated', $event)"
            @deleted="$emit('deleted', $event)"
        />
    </component>
</template>

<script>
import AdIcons from '@/components/partials/icons/AdIcons.vue';
import { changeDateFormat } from '@/helpers/helpers';
import AdCardUserActions from '@components/AdCard/AdCardUserActions.vue';
import FavButton from '@components/partials/buttons/FavButton.vue';
import CompactGallery from '@components/partials/gallery/CompactGallery.vue';
import DoubleGallery from '@components/partials/gallery/DoubleGallery.vue';
import ArrowRight from '@components/partials/icons/ArrowRight.vue';

export default {
    components: {
        AdIcons,
        FavButton,
        AdCardUserActions,
        ArrowRight,
        CompactGallery,
        DoubleGallery,
    },
    props: {
        ad: {
            type: Object,
            required: true,
        },
        agents: {
            type: Array,
            required: false,
        },
        isSelected: {
            type: Boolean,
            required: false,
            default: false,
        },
        rentType: {
            type: String,
            default: 'month',
        },
        hasFavButton: {
            type: Boolean,
            required: false,
            default: true,
        },
        hasOwnerActions: {
            type: Boolean,
            required: false,
            default: false,
        },
        hasRenewButton: {
            type: Boolean,
            required: false,
            default: false,
        },
        hasPostedBy: {
            type: Boolean,
            required: false,
            default: false,
        },
        isMainSearchCard: {
            type: Boolean,
            required: false,
            default: false,
        },
        isFeatured: {
            type: Boolean,
            required: false,
            default: false,
        },
        withDoubleGallery: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            isFavorite: this.ad.auth_user.is_in_favorites,
        };
    },
    computed: {
        priceReducedFormatted() {
            return this.rentType === 'month'
                ? this.ad.rent_cost_month_previous_formatted
                : this.ad.rent_cost_week_previous_formatted;
        },
        priceFormatted() {
            if (this.ad.available_budget_max > 0) {
                if (this.ad.available_budget_min_month_formatted === this.ad.available_budget_max_month_formatted) {
                    return this.rentType === 'month'
                        ? this.ad.available_budget_max_month_formatted
                        : this.ad.available_budget_max_week_formatted;
                }
                return this.rentType === 'month'
                    ? `${this.ad.available_budget_min_month_formatted}-${this.ad.available_budget_max_month_formatted}`
                    : `${this.ad.available_budget_min_week_formatted}-${this.ad.available_budget_max_week_formatted}`;
            }
            return this.rentType === 'month'
                ? this.ad.rent_cost_month_formatted
                : this.ad.rent_cost_week_formatted;
        },
        rentTypeFormatted() {
            return this.rentType === 'month'
                ? 'month'
                : 'week';
        },
    },
    methods: {
        changeDateFormat,
        openAdUrl() {
            window.open(this.ad.ad_url, '_self');
        },
    },
};
</script>
